import React, { useState, useRef, useEffect, useCallback } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, InputPrimitive } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useId,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";

export interface OnInputPinCompleteProps {
  isComplete: boolean;
  value: string;
  /** Test ID for testing frameworks */
  testID?: string;
}

export interface InputPinProps extends ThemeOverrideProps {
  /** Current value of the input pin */
  value?: string;
  /** Function that will be called when the input value changes */
  onChange?: (props: OnInputPinCompleteProps) => void;
  /** Function that will be called when the input is completed */
  onComplete?: (props: OnInputPinCompleteProps) => void;
  /** The length of the code to be input. Default is 4 */
  codeLength?: number;
  /** @deprecated Use codeLength instead */
  length?: number;
  /** Property for changing the size of the input */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /** @deprecated Use disabled and error props instead */
  state?: "default" | "hover" | "disable" | "error";
  /** Property for disabling the control */
  disabled?: boolean;
  /** Property for displaying an error state */
  error?: boolean;
  /** Property for displaying a label above the input */
  label?: string;
  /** Property for displaying an error message */
  errorMessage?: string;
  /** @deprecated Use errorMessage instead */
  errorLabel?: string;
  /** Whether to hide the text (like a password field) */
  secureTextEntry?: boolean;
  /** Show dot placeholders in empty inputs */
  showPlaceholderDots?: boolean;
  /** Expands input cells to fill the width of the container */
  flexibleWidth?: boolean;
  /** Boolean value indicating if the input is filled or not */
  isComplete?: boolean;
  /** The current focused input index */
  currentFocus?: number;
  /** Function to add a ref to an input element */
  addRef?: (index: number) => (element: HTMLInputElement | null) => void;
  /** Test identifier for the component */
  testID?: string;
  /** Accessible label for screen readers (use when no visible label) */
  "aria-label"?: string;
}

export const InputPin: React.FC<InputPinProps> = ({
  value = "",
  onChange,
  onComplete,
  codeLength,
  length = 4,
  size = "md",
  state: externalState,
  disabled: disabledProp,
  error: errorProp,
  label,
  errorMessage,
  errorLabel,
  secureTextEntry = false,
  showPlaceholderDots = true,
  flexibleWidth = false,
  isComplete: isCompleteProp,
  currentFocus,
  addRef,
  testID,
  "aria-label": ariaLabel,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const uniqueId = useId();
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [internalValue, setInternalValue] = useState(value);

  // Support both codeLength (new) and length (deprecated)
  const actualLength = codeLength ?? length;

  // Support both errorMessage (new) and errorLabel (deprecated)
  const errorText = errorMessage ?? errorLabel;

  // Support both new props and deprecated state prop
  // Error state is true if error prop is set, or errorMessage is provided, or state is "error"
  const isError = errorProp ?? (!!errorText || externalState === "error");
  const isDisabled = disabledProp ?? externalState === "disable";
  const isHover = externalState === "hover";
  const sizeStyles = theme.sizing.inputPin(size);
  const inputColors = theme.colors.control.input;

  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  // Sync internal value with external value
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Handle currentFocus prop
  useEffect(() => {
    if (
      currentFocus !== undefined &&
      currentFocus >= 0 &&
      currentFocus < actualLength
    ) {
      inputs.current[currentFocus]?.focus();
    }
  }, [currentFocus, actualLength]);

  // Calculate isComplete status
  const checkIsComplete = useCallback(
    (val: string) => val.length >= actualLength,
    [actualLength]
  );

  // IDs for accessibility
  const labelId = `${uniqueId}-label`;
  const errorId = `${uniqueId}-error`;

  const handleTextChange = (text: string, index: number) => {
    // Filter to only alphanumeric characters
    const sanitizedText = text.replace(/[^0-9a-zA-Z]/g, "");

    // Handle multi-character input (paste on React Native)
    if (sanitizedText.length > 1) {
      const pastedData = sanitizedText.slice(0, actualLength - index);
      const newValue = internalValue.split("");

      // Ensure the array has the correct length
      while (newValue.length < actualLength) {
        newValue.push("");
      }

      // Fill in characters starting from current index
      for (let i = 0; i < pastedData.length && index + i < actualLength; i++) {
        newValue[index + i] = pastedData[i];
      }

      const updatedValue = newValue.slice(0, actualLength).join("");
      setInternalValue(updatedValue);

      const complete = checkIsComplete(updatedValue);
      const changeProps = { isComplete: complete, value: updatedValue };

      onChange?.(changeProps);

      if (complete) {
        onComplete?.(changeProps);
      }

      // Focus the next empty input or the last input
      const nextIndex = Math.min(index + pastedData.length, actualLength - 1);
      inputs.current[nextIndex]?.focus();
      return;
    }

    // Handle single character input
    const char = sanitizedText.slice(-1);
    const newValue = internalValue.split("");

    // Ensure the array has the correct length
    while (newValue.length < actualLength) {
      newValue.push("");
    }

    newValue[index] = char;
    const updatedValue = newValue.slice(0, actualLength).join("");
    setInternalValue(updatedValue);

    const complete = checkIsComplete(updatedValue);
    const changeProps = { isComplete: complete, value: updatedValue };

    onChange?.(changeProps);

    if (complete) {
      onComplete?.(changeProps);
    }

    if (char && index < actualLength - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (!internalValue[index] && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < actualLength - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/[^0-9a-zA-Z]/g, "")
      .slice(0, actualLength);
    setInternalValue(pastedData);

    const complete = checkIsComplete(pastedData);
    const changeProps = { isComplete: complete, value: pastedData };

    onChange?.(changeProps);

    if (complete) {
      onComplete?.(changeProps);
    }

    // Focus the last filled input or the first empty one
    const nextIndex = Math.min(pastedData.length, actualLength - 1);
    inputs.current[nextIndex]?.focus();
  };

  // Handler for storing refs that can be provided to addRef prop
  const handleRef = (index: number) => (element: HTMLInputElement | null) => {
    inputs.current[index] = element;
    addRef?.(index)(element);
  };

  const pinItems = Array.from({ length: actualLength }).map((_, index) => {
    const char = internalValue[index] || "";
    const isFocused = focusedIndex === index;

    let backgroundColor = inputColors.bg;
    let borderColor = inputColors.border;

    if (isDisabled) {
      backgroundColor = inputColors.bgDisable;
      borderColor = inputColors.borderDisable;
    } else if (isError) {
      borderColor = theme.colors.border.alert;
      backgroundColor = isFocused
        ? theme.colors.control.focus.bg
        : inputColors.bg;
    } else if (isFocused) {
      backgroundColor = theme.colors.control.focus.bg;
      borderColor = theme.colors.content.brand.secondary;
    } else if (isHover) {
      backgroundColor = inputColors.bgHover;
      borderColor = inputColors.borderHover;
    } else {
      backgroundColor = inputColors.bg;
      borderColor = inputColors.border;
    }

    const textColor = isDisabled ? inputColors.textDisable : inputColors.text;

    return (
      <Box
        key={index}
        width={flexibleWidth ? undefined : sizeStyles.size}
        height={sizeStyles.size}
        flex={flexibleWidth ? 1 : undefined}
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={sizeStyles.borderWidth}
        borderRadius={sizeStyles.radius}
        alignItems="center"
        justifyContent="center"
        hoverStyle={
          !isDisabled && !isFocused && !isError
            ? {
                backgroundColor: inputColors.bgHover,
                borderColor: inputColors.borderHover,
              }
            : undefined
        }
      >
        <InputPrimitive
          ref={
            addRef
              ? handleRef(index)
              : (el: any) => (inputs.current[index] = el)
          }
          value={char}
          placeholder={showPlaceholderDots && !isFocused ? "•" : undefined}
          onChangeText={(text: string) => handleTextChange(text, index)}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex(null)}
          onKeyDown={(e: any) => handleKeyDown(e, index)}
          disabled={isDisabled}
          secureTextEntry={secureTextEntry}
          color={textColor}
          placeholderTextColor={inputColors.placeholder}
          fontSize={sizeStyles.fontSize}
          style={{ textAlign: "center" }}
          maxLength={1}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={
            ariaLabel
              ? `${ariaLabel} digit ${index + 1}`
              : `PIN digit ${index + 1}`
          }
          aria-invalid={isError}
          aria-describedby={isError && errorText ? errorId : undefined}
          data-testid={
            testID ? `${testID}-input-${index}` : `input-pin-input-${index}`
          }
        />
      </Box>
    );
  });

  return (
    <Box
      flexDirection="column"
      gap={sizeStyles.fieldGap}
      data-testid={testID || "input-pin"}
      role="group"
      aria-labelledby={label ? labelId : undefined}
      aria-label={!label && ariaLabel ? ariaLabel : undefined}
    >
      {label && (
        <Text
          id={labelId}
          color={theme.colors.content.secondary}
          fontSize={sizeStyles.fontSize - 2}
          fontWeight="500"
        >
          {label}
        </Text>
      )}
      <Box
        flexDirection="row"
        gap={sizeStyles.gap}
        onPaste={handlePaste}
        width={flexibleWidth ? "100%" : undefined}
      >
        {pinItems}
      </Box>
      {isError && errorText && (
        <Text
          id={errorId}
          role="alert"
          color={theme.colors.content.alert.primary}
          fontSize={sizeStyles.fontSize - 2}
        >
          {errorText}
        </Text>
      )}
    </Box>
  );
};
